import { useEffect } from 'react';

import { useAtom } from 'jotai';
import { Redirect, Route, Switch, useRouteMatch } from 'react-router';

import { useGetPracticeGroups } from '@/react-query/insights/usePracticeGroups';
import { usePreferences } from '@/react-query/usePreferences';

import routes from '../../../routes/routes.constants';

import { hygieneProviderBenchmarkAtom } from './atoms';
import { useGetFilteredHygieneData } from './helpers/data/getHygieneData';
import {
  useCalculateHygieneBenchmark,
  useFilteredHygieneInsightsData,
} from './helpers/filterHygieneData';
import { AIBenchmarks } from './mainDashboard/clinical/AIBenchmarks';
import { HygieneInsights } from './mainDashboard/hygiene/HygieneInsights';
import { PracticeBenchmarks } from './practiceDrilldown/clinical/PracticeBenchmarks';
import { PracticeHygiene } from './practiceDrilldown/hygiene/PracticeHygiene';
import { ProviderBenchmarks } from './providerDrilldown/clinical/ProviderBenchmarks';
import { ProviderHygiene } from './providerDrilldown/hygiene/ProviderHygiene';
import { RegionBenchmarks } from './regionDrilldown/clinical/RegionBenchmarks';
import { RegionHygiene } from './regionDrilldown/hygiene/RegionHygiene';

export const Insights = () => {
  const { path } = useRouteMatch();
  const { data: prefs, isLoading: isPrefsLoading } = usePreferences(true);
  const [, setHygieneProviderBenchmark] = useAtom(hygieneProviderBenchmarkAtom);

  const { data: practiceGroups } = useGetPracticeGroups();

  const { filteredData: hygieneData, isLoading: isHygieneLoading } =
    useGetFilteredHygieneData();

  const {
    providerRates: hygieneProviderRates,
    practiceRates: hygienePracticeRates,
    regionRates: hygieneRegionRates,
  } = useFilteredHygieneInsightsData(hygieneData, practiceGroups?.data);

  const {
    lowerBenchmark: hygieneLowerBenchmark,
    upperBenchmark: hygieneUpperBenchmark,
  } = useCalculateHygieneBenchmark(hygieneData);

  useEffect(() => {
    if (hygieneLowerBenchmark && hygieneUpperBenchmark) {
      setHygieneProviderBenchmark({
        lowerBenchmark: hygieneLowerBenchmark,
        upperBenchmark: hygieneUpperBenchmark,
      });
    }
  }, [
    hygieneLowerBenchmark,
    hygieneUpperBenchmark,
    setHygieneProviderBenchmark,
  ]);

  return (
    <Switch>
      {/* This is kind of a hack to redirect because preferences are not loaded on app start */}
      {!prefs.enableInsightsPOC && !isPrefsLoading && (
        <Redirect to={routes.SWITCHBOARD} />
      )}
      <Route
        path={`${path}/hygiene/region/:regionId/practice/:practiceId/provider/:providerId`}
      >
        <ProviderHygiene hygieneProviderRates={hygieneProviderRates} />
      </Route>
      <Route
        path={`${path}/clinical/region/:regionId/practice/:practiceId/provider/:providerId`}
      >
        <ProviderBenchmarks />
      </Route>
      <Route path={`${path}/hygiene/region/:regionId/practice/:practiceId`}>
        <PracticeHygiene hygienePracticeRates={hygienePracticeRates} />
      </Route>
      <Route path={`${path}/clinical/region/:regionId/practice/:practiceId`}>
        <PracticeBenchmarks />
      </Route>
      <Route path={`${path}/hygiene/region/:regionId`}>
        <RegionHygiene hygieneRegionRates={hygieneRegionRates} />
      </Route>
      <Route path={`${path}/clinical/region/:regionId`}>
        <RegionBenchmarks />
      </Route>
      <Route path={`${path}/hygiene`}>
        <HygieneInsights
          hygieneRates={{
            providerRates: hygieneProviderRates,
            practiceRates: hygienePracticeRates,
            regionRates: hygieneRegionRates,
          }}
          isHygieneLoading={isHygieneLoading}
        />
      </Route>
      <Route path={`${path}/clinical`}>
        <AIBenchmarks />
      </Route>
      <Redirect to={`${path}/clinical`} />
    </Switch>
  );
};
